import React from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  Box,
  Display,
  FlexDirection,
  JustifyContent,
  Space,
  RadioGroup,
  Heading5,
  Heading6,
  Body1,
  Text,
  FontWeight,
  Icon,
  IconVariant,
  Heading1,
  Visible,
  Image,
  Color,
  BorderStyle,
} from '@gousto-internal/citrus-react'
import { useDecision } from '@optimizely/react-sdk'
import { useDispatch, useSelector } from 'react-redux'

import { FlagProblematicRendering } from '@library/e2e-util-problematic-rendering'

import { basketNumPortionChange, portionSizeSelectedTracking } from 'actions/basket'
import { completeWizardBoxSize } from 'actions/trackingKeys'
import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import { useIsOptimizelyFeatureEnabled } from 'containers/OptimizelyRollouts'
import { NumberOfPortions, useBasket } from 'routes/Menu/domains/basket'
import { StickyCTA } from 'routes/Signup/Components/StickyCTA/StickyCTA'
import { trackSignupWizardAction } from 'routes/Signup/signupActions'
import { signupConfig } from 'routes/Signup/signupConfig'
import { getNumPortions } from 'selectors/basket'

import { CarouselBoxWrapper } from './BoxSizeStep.styled'
import { PrimaryButton } from './PrimaryButton'

type BoxSizeStepProps = {
  next: () => void
}

const StyledRadioContainer = styled.div`
  label {
    align-items: flex-start;
    color: initial;
  }

  label:first-of-type {
    margin-bottom: 16px;
  }
`

export const BoxSizeStep = ({ next }: BoxSizeStepProps) => {
  const isPortionClarityEnabled = useIsOptimizelyFeatureEnabled(
    'beetroots_portion_clarity_web_enabled',
  )
  const isWizardFloatingCTAEnabled = useIsOptimizelyFeatureEnabled(
    'beetroots_wizard_floating_cta_enabled_v1',
  )
  /**
   * Experiment to test allocation for Oprimizely FX implementation
   * Cleanup ticket - https://gousto.atlassian.net/browse/FEF-1188
   */
  useDecision('fef_ofx_box_type_test')

  const dispatch = useDispatch()
  const { boxSizeTypes, title, portionClairtyBoxSizeTypes, portionClarityTitle } =
    signupConfig.boxSizeStep
  const { setNumPortions } = useBasket()
  const numPortions = useSelector(getNumPortions)

  const handlePrimaryButtonClick = (value: NumberOfPortions) => {
    dispatch(basketNumPortionChange(value))
    dispatch(portionSizeSelectedTracking(value))
    setNumPortions(value)
    dispatch(
      trackSignupWizardAction(completeWizardBoxSize, {
        box_size: value,
      }),
    )
    next()
  }

  const renderCarouselItems = () =>
    boxSizeTypes.map(
      ({
        heading,
        suitableFor,
        recipeInfoTextStart,
        recipeInfoStrongText,
        recipeInfoTextEnd,
        ctaText,
        value,
      }) => (
        <CarouselBoxWrapper
          display={Display.Flex}
          flexDirection={[FlexDirection.Column]}
          paddingH={4}
          paddingV={4}
          key={`${heading}-carousel-item`}
          data-testid={`${heading}-carousel-item`}
          borderColor={Color.ColdGrey_100}
          borderStyle={BorderStyle.Solid}
          borderWidth={0.5}
          width="100%"
        >
          <Heading5>{heading}</Heading5>
          <Space size={4} />
          <Box paddingBottom={2} data-testid="suitable-title">
            <Text size={4} display={Display.Inline}>
              {recipeInfoTextStart}
            </Text>
            <Text size={4} display={Display.Inline}>
              {recipeInfoStrongText}
            </Text>
            <Text size={4} display={Display.Inline}>
              {recipeInfoTextEnd}
            </Text>
          </Box>
          <Body1>
            <Text display={Display.Inline} size={2}>
              Suitable for:
            </Text>
            <Space size={2} direction="vertical" />
            {suitableFor.map((label: string) => (
              <Box
                key={`portion-clarity-radio-suitable-for-${label}`}
                display={Display.Flex}
                alignItems={AlignItems.Center}
              >
                <Icon name="tick" variant={IconVariant.Confirmation} />
                <Space direction="horizontal" size={2} />
                <Text size={2}>{label}</Text>
              </Box>
            ))}
          </Body1>
          <Space size={5} />
          <PrimaryButton
            value={value}
            onPrimaryButtonClick={handlePrimaryButtonClick}
            ctaText={ctaText}
          />
        </CarouselBoxWrapper>
      ),
    )

  const renderPortionClarityCarouselItems = () =>
    portionClairtyBoxSizeTypes.map(
      ({ heading, suitableFor, ctaText, value, webpImage, pngImage }) => (
        <>
          <CarouselBoxWrapper
            display={Display.Flex}
            flexDirection={[FlexDirection.Column]}
            paddingH={4}
            paddingV={4}
            data-testid={`portion-clarity-${heading}-carousel-item`}
            borderColor={Color.ColdGrey_100}
            borderStyle={BorderStyle.Solid}
            borderWidth={0.5}
            width="100%"
          >
            <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
              <Heading6> {heading}</Heading6>
              <Box style={{ marginTop: '-25px' }}>
                <Image srcSet={webpImage} height="64px" width="auto" src={pngImage} alt="" />
              </Box>
            </Box>
            <Body1>
              <Text display={Display.Inline} size={2}>
                Suitable for:
              </Text>
              <Space size={2} direction="vertical" />
              {suitableFor.map((label: string) => (
                <Box
                  key={`portion-clarity-radio-suitable-for-${label}`}
                  display={Display.Flex}
                  alignItems={AlignItems.Center}
                >
                  <Icon name="tick" variant={IconVariant.Confirmation} />
                  <Space direction="horizontal" size={2} />
                  <Text size={2}>{label}</Text>
                </Box>
              ))}
            </Body1>
            <Space size={5} />
            <PrimaryButton
              value={value}
              onPrimaryButtonClick={handlePrimaryButtonClick}
              ctaText={ctaText}
            />
          </CarouselBoxWrapper>
        </>
      ),
    )

  const handleStickyCTAClick = () => {
    dispatch(portionSizeSelectedTracking(numPortions))
    dispatch(
      trackSignupWizardAction(completeWizardBoxSize, {
        box_size: numPortions,
      }),
    )
    next()
  }

  const renderRadioItems = () => {
    const getRadioLabel = ({
      heading,
      suitableFor,
      recipeInfoTextStart,
      recipeInfoStrongText,
      recipeInfoTextEnd,
    }: typeof boxSizeTypes[number]) => (
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        paddingH={3}
        paddingRight={0}
        data-testid={`${heading}-radio-label`}
      >
        <Heading5> {heading}</Heading5>
        <Space size={2} direction="vertical" />
        <Body1>
          <Text display={Display.Inline}>{recipeInfoTextStart}</Text>
          <Text display={Display.Inline} fontWeight={FontWeight.Bold}>
            {recipeInfoStrongText}
          </Text>
          <Text display={Display.Inline}>{recipeInfoTextEnd}</Text>
        </Body1>
        <Space size={4} direction="vertical" />
        <Body1>
          <Text display={Display.Inline} size={2}>
            Suitable for:
          </Text>
          <Space size={2} direction="vertical" />
          {suitableFor.map((label: string) => (
            <Box
              key={`radio-suitable-for-${label}`}
              display={Display.Flex}
              alignItems={AlignItems.Center}
            >
              <Icon name="tick" variant={IconVariant.Confirmation} />
              <Space direction="horizontal" size={2} />
              <Text size={2}>{label}</Text>
            </Box>
          ))}
        </Body1>
      </Box>
    )
    const getRadioOptions = () =>
      boxSizeTypes.map((boxSize) => ({
        name: 'boxSizes',
        value: boxSize.value,
        label: () => getRadioLabel(boxSize),
      }))

    const handleRadioBoxSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const portionSelected = parseInt(event.target.value, 10)
      setNumPortions(portionSelected)
      dispatch(basketNumPortionChange(portionSelected))
    }

    return (
      <StyledRadioContainer>
        <RadioGroup
          value={numPortions}
          outline
          options={getRadioOptions()}
          onChange={handleRadioBoxSizeChange}
        />
      </StyledRadioContainer>
    )
  }

  const renderPortionClarityRadioItems = () => {
    const getRadioLabel = ({
      heading,
      suitableFor,
      webpImage,
      pngImage,
    }: typeof portionClairtyBoxSizeTypes[number]) => (
      <>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          paddingH={3}
          paddingRight={0}
          data-testid={`portion-clarity-${heading}-radio-label`}
          width="100%"
        >
          <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
            <Heading5> {heading}</Heading5>
            <Box style={{ marginTop: '-25px' }}>
              <Image srcSet={webpImage} height="64px" width="auto" src={pngImage} alt="" />
            </Box>
          </Box>
          <Body1>
            <Text display={Display.Inline} size={2}>
              Suitable for:
            </Text>
            <Space size={2} direction="vertical" />
            {suitableFor.map((label: string) => (
              <Box
                key={`portion-clarity-radio-suitable-for-${label}`}
                display={Display.Flex}
                alignItems={AlignItems.Center}
              >
                <Icon name="tick" variant={IconVariant.Confirmation} />
                <Space direction="horizontal" size={2} />
                <Text size={2}>{label}</Text>
              </Box>
            ))}
          </Body1>
        </Box>
        <Space direction="vertical" size={4} />
      </>
    )

    const getRadioOptions = () =>
      portionClairtyBoxSizeTypes.map((boxSize) => ({
        name: 'boxSizes',
        value: boxSize.value,
        label: () => getRadioLabel(boxSize),
      }))

    const handleRadioBoxSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const portionSelected = parseInt(event.target.value, 10)
      setNumPortions(portionSelected)
      dispatch(basketNumPortionChange(portionSelected))
    }

    return (
      <StyledRadioContainer>
        <RadioGroup
          value={numPortions}
          outline
          options={getRadioOptions()}
          onChange={handleRadioBoxSizeChange}
        />
      </StyledRadioContainer>
    )
  }

  const renderBoxStepContent = () => {
    if (isWizardFloatingCTAEnabled) {
      const boxSize = numPortions === 2 ? 'regular' : 'large'

      return (
        <>
          <Visible display={[Display.Block, Display.None]}>
            {isPortionClarityEnabled ? renderPortionClarityRadioItems() : renderRadioItems()}
            <StickyCTA onClick={handleStickyCTAClick}>Choose {boxSize} box</StickyCTA>
          </Visible>
          <Visible display={[Display.None, Display.Block]}>
            <Box
              display={[Display.Block, Display.Block, Display.Flex]}
              justifyContent={JustifyContent.SpaceBetween}
            >
              {isPortionClarityEnabled
                ? renderPortionClarityCarouselItems()
                : renderCarouselItems()}
            </Box>
          </Visible>
        </>
      )
    }

    return (
      <Box
        display={[Display.Block, Display.Block, Display.Flex]}
        justifyContent={JustifyContent.SpaceBetween}
      >
        {isPortionClarityEnabled ? renderPortionClarityCarouselItems() : renderCarouselItems()}
      </Box>
    )
  }

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      justifyContent={JustifyContent.SpaceBetween}
      alignItems={AlignItems.Center}
      data-testing="signupBoxSizeStep"
    >
      <FlagProblematicRendering label="boxSizeStepRenderingProblem" />
      <Box width="100%">
        <Heading1>{isPortionClarityEnabled ? portionClarityTitle : title}</Heading1>
        <Space size={4} direction="vertical" />
        {renderBoxStepContent()}
      </Box>
      <RibbonTriggerContainer name="wizard-box-size" />
    </Box>
  )
}
